import qs from 'qs';

import { EntryVersion } from '@/components/cms/types/entry';
import { cmsApiEndpoint, isBuildPhase, isProd } from '@/constant/env';
import {
  defaultLocale,
  localeBuildSeparately,
  localeBuildTogether,
} from '@/lib/i18n/dictionaries';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
export type ApiResponse<T> = {
  data: T | null;
  error: string | null;
};

export type ApiCallProps = {
  url: string;
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal | null;
};

export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retries = 6,
  backoff = 3000
): Promise<Response> {
  try {
    const res = await fetch(url, options);
    if (!res.ok) {
      throw new Error(`Fetch failed with status ${res.status}`);
    }
    return res;
  } catch (err) {
    if (retries > 0) {
      console.warn(`Retrying ${url} (${retries} left)...`);
      await new Promise((r) => setTimeout(r, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    } else {
      throw new Error(`Failed to fetch ${url}: ${err}`);
    }
  }
}

export async function apiCall<T>({
  url,
  method,
  body,
  headers,
  signal,
}: ApiCallProps): Promise<ApiResponse<T> | undefined> {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  const options: RequestInit = {
    method,
    headers: body instanceof FormData ? headers : defaultHeaders,
    body: body instanceof FormData ? body : JSON.stringify(body),
    signal,
  };

  try {
    let response;
    if (isBuildPhase) {
      response = await fetchWithRetry(url, options);
    } else {
      response = await fetch(url, options);
    }

    if (!response.ok) {
      console.error(
        'Error:',
        response.statusText,
        response.status,
        response.url
      );
      // Check if response body
      try {
        const errorBody = await response.json();
        return { data: null, error: errorBody };
      } catch (error) {
        return {
          data: null,
          error: response.statusText || `${response.status}`,
        };
      }
    }
    const data = (await response.json()) as T;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: String(error) };
  }
}

export function withStrapiHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const token = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;

  let headersWithStrapi = {
    Authorization: `Bearer ${token}`,
    'Strapi-Response-Format': 'v4',
  };

  if (headers) {
    headersWithStrapi = { ...headers, ...headersWithStrapi };
  }

  return headersWithStrapi;
}

export async function apiCallStrapi<ResponseT>({
  path,
  query,
  method,
  headers = {},
  body,
  status,
  signal,
}: {
  path: string;
  query?: Record<string, unknown>;
  method?: HttpMethod;
  headers?: ApiCallProps['headers'];
  body?: ApiCallProps['body'];
  status?: EntryVersion;
  signal?: AbortSignal | null;
}) {
  const locale = defaultLocale;

  const { filters, ...queryRest } = query || {};

  // TODO replace with simple query string ourselfs
  const queryString = qs.stringify({
    status: status ?? (isProd ? 'published' : 'draft'),
    filters: {
      locale: {
        $in: localeBuildSeparately.includes(locale)
          ? [locale]
          : localeBuildTogether,
      },
      ...(filters || {}),
    },
    ...queryRest,
  });

  const requestUrl =
    cmsApiEndpoint + path + (queryString ? `?${queryString}` : '');

  const response = await apiCall<ResponseT>({
    url: requestUrl,
    method,
    body,
    headers: withStrapiHeaders(headers),
    signal,
  });

  return response?.data;
}
